import { useCallback, useEffect, useState } from 'react';

import isUserTypeCustomer from 'src/auth/helpers';
import { isCancelledRequestError } from 'src/components/Trade/TradeList/TradeListTable/helpers/dataBatching';
import useBatchedFetchErrorState from 'src/hooks/useBatchedFetchErrorState';

import { getViewerMeters } from '../meterFetching';

/**
 * Fetches the viewer's meters when the user is a customer, tracking loading state.
 * The in-flight fetch is aborted on unmount or when the dependencies change. When the
 * fetch returns a partial result (some meters could not be loaded), a non-blocking
 * warning flash is surfaced so the user knows to refresh before accepting a trade.
 * @param {object} environment - Relay environment used to run the query.
 * @param {string} userType - The viewer's user type; meters are only fetched for customers.
 * @returns {{meters: Array<object>, metersLoading: boolean, handleErrorDismiss: Function,
 * error: Error|null, isRetrying: boolean, handleRetry: Function}}
 * The fetched meters and loading flag.
 */
const useViewerMeters = (environment, userType) => {
  const [meters, setMeters] = useState([]);
  const [metersLoading, setMetersLoading] = useState(true);

  const {
    retryNonce,
    clearError,
    resolveRetry,
    handleFetchError,
    error,
    isRetrying,
    handleRetry,
  } = useBatchedFetchErrorState();

  const handleErrorDismiss = useCallback(() => {
    clearError();
  }, [clearError]);

  useEffect(() => {
    if (!isUserTypeCustomer(userType)) {
      setMetersLoading(false);
      return undefined;
    }

    const abortController = new AbortController();
    const { signal } = abortController;
    setMetersLoading(true);

    getViewerMeters({ environment, signal })
      .then((fetchedMeters) => {
        if (!signal.aborted) {
          setMeters(fetchedMeters);
          resolveRetry();
        }
      })
      .catch((fetchError) => {
        if (!signal.aborted && !isCancelledRequestError(fetchError)) {
          handleFetchError(fetchError);
        }
      })
      .finally(() => {
        if (!signal.aborted) {
          setMetersLoading(false);
        }
      });

    return () => {
      abortController.abort();
    };
  }, [environment, userType, resolveRetry, handleFetchError, retryNonce]);

  return {
    meters,
    metersLoading,
    handleErrorDismiss,
    error,
    isRetrying,
    handleRetry,
  };
};

export default useViewerMeters;
